import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buildQueryString } from '@/lib/api/client';
import { recipientsFetch } from '@/lib/recipients/api';
import { toast } from 'sonner';
import { recycleBinKeys, recipientKeys } from '@/hooks/api/query-keys';
import type { PaginatedResult } from '@/hooks/api/pagination';
import { runPerId } from '@/lib/recipients/run-per-id';

// ============ Types ============

export interface DeletedRecipient {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  department?: string;
  deletedAt: string;
  deletedById?: string;
}

export interface RecycleBinResponse {
  data: DeletedRecipient[];
  total: number;
  // #205 — absolute count of items in the bin, ignoring search/date filters.
  unfilteredTotal?: number;
  // #249/F1 — server aggregates for the "Oldest Record" and "Deleted Today"
  // stat cards. The page used to reduce these over `data` (the current page of
  // <=25 rows), so both moved with pagination and one card's sort toggle
  // silently zeroed the other.
  oldestDeletedAt?: string;
  unfilteredOldestDeletedAt?: string;
  deletedToday?: number;
  unfilteredDeletedToday?: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * What the page consumes. Every field is guaranteed present here — this is the
 * normalization layer, so the page never writes `?? 0` / `?? ''`.
 *
 * `hasTodayStat` distinguishes "0 deleted today" from "we did not ask": the BE
 * only issues the today aggregates when a `todayStart` boundary is supplied,
 * and a page that cannot tell the two apart renders a fabricated zero.
 */
export interface RecycleBinPageResult extends PaginatedResult<DeletedRecipient> {
  unfilteredTotal: number;
  /** ISO, '' when the filtered set is empty. */
  oldestDeletedAt: string;
  /** ISO, '' when the bin is empty. */
  unfilteredOldestDeletedAt: string;
  deletedToday: number;
  unfilteredDeletedToday: number;
  hasTodayStat: boolean;
}

/** Guaranteed-shape placeholder for the pre-resolve render. */
export function emptyRecycleBinPage(limit = 25): RecycleBinPageResult {
  return {
    data: [],
    total: 0,
    unfilteredTotal: 0,
    page: 1,
    limit,
    totalPages: 1,
    oldestDeletedAt: '',
    unfilteredOldestDeletedAt: '',
    deletedToday: 0,
    unfilteredDeletedToday: 0,
    hasTodayStat: false,
  };
}

// ============ Query Hooks ============

export function useRecycleBin(options?: {
  page?: number;
  limit?: number;
  search?: string;
  // #123/#141 — server-side counter-card filter + sort toggle
  deletedFrom?: string;
  deletedTo?: string;
  deletedAtSort?: 'ASC' | 'DESC';
  /**
   * #249 — browser-local start-of-day, sent so the "Deleted Today" stat uses
   * the SAME boundary the Today facet filters on. Without it the BE would have
   * to invent a "today" in its own timezone and the two could disagree.
   */
  todayStart?: string;
}) {
  const defaultLimit = options?.limit ?? 25;
  const askedForToday = Boolean(options?.todayStart);
  return useQuery({
    queryKey: recycleBinKeys.list(options),
    queryFn: async () => {
      const qs = buildQueryString(options as Record<string, unknown>);
      return recipientsFetch<RecycleBinResponse>(`recycle-bin${qs}`);
    },
    select: (raw): RecycleBinPageResult => {
      const limit = raw.limit ?? defaultLimit;
      const total = raw.total ?? 0;
      return {
        data: raw.data ?? [],
        total,
        // #205 — normalize here so the page consumes a guaranteed number
        // (never `?? []`/`?? {}` in a page/component). Falls back to the
        // filtered total for older BE payloads that omit the field.
        unfilteredTotal: raw.unfilteredTotal ?? total,
        // #249/F1 — same discipline for the four new aggregates. No fallback
        // to a browser-side reduce: an absent field means "unknown", and the
        // page renders that as unknown rather than as a number it made up.
        oldestDeletedAt: raw.oldestDeletedAt ?? '',
        unfilteredOldestDeletedAt: raw.unfilteredOldestDeletedAt ?? '',
        deletedToday: raw.deletedToday ?? 0,
        unfilteredDeletedToday: raw.unfilteredDeletedToday ?? 0,
        hasTodayStat:
          askedForToday && typeof raw.unfilteredDeletedToday === 'number',
        page: raw.page ?? 1,
        limit,
        totalPages: raw.totalPages ?? Math.max(1, Math.ceil(total / limit)),
      };
    },
  });
}

// ============ Restore ============

/**
 * One group membership a restore did not bring back (vCRO wave 0). A person's
 * memberships survive the recycle bin; where they held two groups of one
 * exclusive category, the group they joined first keeps them.
 */
export interface SkippedMembership {
  recipientId: string;
  groupId: string;
  groupName: string;
  categoryId: string;
  categoryName: string;
  keptGroupId: string;
  keptGroupName: string;
  reason: 'group_category_exclusive';
}

/** What POST /recycle-bin/:id/restore returns: the row, and what it left out. */
export interface RestoredRecipient {
  id: string;
  skippedMemberships?: SkippedMembership[];
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * The line a restore adds when it left memberships out, or null when it left
 * none. One skip names the group, the category and the group that kept them;
 * several say how many. Group and category names are the customer's own text.
 */
export function restoreSkipMessage(
  restored: RestoredRecipient | undefined,
  t: Translate,
): string | null {
  const skipped = restored?.skippedMemberships ?? [];
  if (skipped.length === 0) return null;
  if (skipped.length === 1) {
    const [only] = skipped;
    return t('toast.recipientRestoredSkippedNamed', {
      group: only.groupName,
      category: only.categoryName,
      kept: only.keptGroupName,
    });
  }
  return t('toast.recipientRestoredSkipped', { count: skipped.length });
}

// ============ Mutation Hooks ============

export function useRestoreRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<RestoredRecipient>(`recycle-bin/${id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recycleBinKeys.all() });
      qc.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function usePermanentDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<void>(`recycle-bin/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recycleBinKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

/**
 * FE16 (audit 2026-09-25) — every id in the bin matching `filters` (the same
 * search / Today facet the list shows), paged through at the API's maximum
 * page size. "Select all N results" means these, not the rows on screen.
 */
export async function collectRecycleBinIds(filters: {
  search?: string;
  deletedFrom?: string;
  deletedTo?: string;
  deletedAtSort?: 'ASC' | 'DESC';
}): Promise<string[]> {
  const limit = 500; // PaginationDto @Max(500)
  const ids: string[] = [];
  for (let page = 1; ; page++) {
    const res = await recipientsFetch<RecycleBinResponse>(
      `recycle-bin${buildQueryString({ ...filters, page, limit } as Record<string, unknown>)}`,
    );
    const rows = res.data ?? [];
    ids.push(...rows.map((row) => row.id));
    if (rows.length < limit || ids.length >= (res.total ?? 0)) return ids;
  }
}

/**
 * FE16 — bulk Restore. The API restores one id per request, so the batch
 * runs every request (a few at a time) and resolves with what happened to
 * each — it never rejects, and the caller reports "N of M". The caches are
 * refreshed once, when the batch ends.
 */
export function useBulkRestoreRecipients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      runPerId(ids, (id) =>
        recipientsFetch<{ restored: boolean }>(`recycle-bin/${id}/restore`, { method: 'POST' }),
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: recycleBinKeys.all() });
      qc.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

/** FE16 — bulk permanent Delete; same contract as useBulkRestoreRecipients. */
export function useBulkPermanentDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      runPerId(ids, (id) => recipientsFetch<void>(`recycle-bin/${id}`, { method: 'DELETE' })),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: recycleBinKeys.all() });
    },
  });
}

/**
 * POST /recipients/recycle-bin/purge — what the API did (R9): `purged` were
 * deleted; `blocked` are kept because other records still reference them,
 * counted per table in `blockedBy`.
 */
export interface PurgeRecycleBinResult {
  purged: number;
  blocked: number;
  blockedBy: Record<string, number>;
}

/**
 * F12 (audit 2026-09-25) — how many records a purge of everything deleted
 * before `deletedBefore` would take: the list's own `deletedTo` filter, one
 * row, its total. Disabled until a cutoff is given (the dialog is open).
 */
export function useRecycleBinPurgeCount(deletedBefore: string | undefined) {
  return useQuery({
    queryKey: [...recycleBinKeys.all(), 'purge-count', deletedBefore],
    queryFn: async () => {
      const qs = buildQueryString({ page: 1, limit: 1, deletedTo: deletedBefore });
      const res = await recipientsFetch<{ total?: number }>(`recycle-bin${qs}`);
      return res.total ?? 0;
    },
    enabled: Boolean(deletedBefore),
    staleTime: 0,
  });
}

export function usePurgeRecycleBin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (olderThanDays?: number) =>
      recipientsFetch<PurgeRecycleBinResult>('recycle-bin/purge', {
        method: 'POST',
        body: JSON.stringify({ olderThanDays: olderThanDays ?? 30 }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recycleBinKeys.all() });
    },
    // No toast here: the page reports a failure once (a hook toast on top of
    // the page's was the "double toasts" defect).
  });
}
